import { ReactNode } from "react";

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: ReactNode;
}

/**
 * Responsive page header.
 *
 * Desktop (≥md): title + description on the left, actions on the right (single row).
 * Mobile  (<md): title + description stacked on top, actions on a full-width row
 * underneath. Action buttons grow to share the row evenly and wrap if they
 * can't all fit, so nothing is clipped off-screen.
 */
export function PageHeader({ title, description, children }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 min-w-0 max-w-full">
      <div className="min-w-0 max-w-full">
        <h1 className="text-xl md:text-3xl font-bold text-foreground tracking-tight truncate">
          {title}
        </h1>
        {description && (
          <p className="text-sm md:text-base text-muted-foreground mt-1 line-clamp-2 md:line-clamp-none">
            {description}
          </p>
        )}
      </div>
      {children && (
        <div
          className="
            flex flex-wrap items-stretch gap-2 min-w-0 max-w-full
            [&>*]:flex-1 [&>*]:min-w-0 [&>*]:basis-[calc(50%-0.25rem)]
            md:flex-nowrap md:[&>*]:flex-none md:[&>*]:basis-auto
            md:items-center
          "
        >
          {children}
        </div>
      )}
    </div>
  );
}
